





























































































































































































import { Component, Vue } from 'vue-property-decorator';
import SubscriptionService from '@/services/SubscriptionService';
import CompanyService from '@/services/CompanyService';
import MySubscriptionDetailsDTO from '@/dto/subscription/MySubscriptionDetailsDTO';
import Application from "@/state/Application";
import {processError} from "@/utils/ComponentUtils";
import {ChargeComponent} from "@/dto/subscription/SublimeChargeDTO";
import Workspaces from "@/state/Workspaces";
import {WorkspaceType} from "@/dto/auth/Workspace";
import SubscriptionOffer from "./SubscriptionOffer.vue";
import RouteNames from "@/router/RouteNames";
import AddressDTO from "@/dto/AddressDTO";
import BranchDto, {CreateBranchPayload, LocationType} from "@/components/profile/branches/BranchDtos";
import PortalAddress from "@/components/profile/branches/PortalAddress.vue";
import AuthService from '@/services/AuthService';
import Auth from '@/state/Auth';

interface AvailableSubscription {
  component: ChargeComponent;
  name: string;
}

@Component({
  components: {
    SubscriptionOffer,
    PortalAddress
  }
})
export default class MySubscriptions extends Vue {
  private subscriptions: MySubscriptionDetailsDTO[] = [];
  private availableSubscriptions: AvailableSubscription[] = [];
  private actualAddress: AddressDTO = new AddressDTO();
  private headquarterData: BranchDto | null = null;
  
  // Tab state
  private activeTab: 'offer' | 'actual' = 'offer';
  
  // Callback handling state
  private isProcessingCallback = false;
  private callbackAction: string | null = null;
  private callbackComponent: ChargeComponent | null = null;
  private pollInterval: number | null = null;
  
  // Company dropdown state
  private openDropdownIndex: number | null = null;

  // Fixed list of available subscriptions to check
  private readonly AVAILABLE_SUBSCRIPTION_LIST: AvailableSubscription[] = [
    { component: ChargeComponent.SUBLIME_DOCS, name: 'Sublime Docs' }
  ];

  // Computed property to determine if tabs should be shown
  get shouldShowTabs(): boolean {
    return this.subscriptions.length > 0;
  }

  async mounted() {
    // Check for callback parameters
    const action = this.$route.query.action as string;
    const sid = this.$route.query.sid as string;
    const component = this.$route.query.component as string;
    
    if (action && sid && component) {
      this.handleCallback(action, sid, component as ChargeComponent);
      return;
    }
    
    await this.loadSubscriptions();
    this.determineAvailableSubscriptions();
  }
  
  beforeDestroy() {
    // Clean up polling interval when component is destroyed
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
    }
  }

  private async loadSubscriptions() {
    this.$loadOn();
    return SubscriptionService.getMySubscriptions().then(
        ok => {
          this.$loadOff();
          this.subscriptions = ok.data;
          return Promise.resolve(ok);
        },
        err => {
          processError(err, this);
          return Promise.reject(err);
        }
    )
  }

  private determineAvailableSubscriptions() {
    // Get list of components user already has
    const subscribedComponents = new Set<ChargeComponent>();
    this.subscriptions.forEach(sub => {
      sub.components.forEach(comp => {
        subscribedComponents.add(comp.component);
      });
    });

    // Filter available subscriptions to show only those not subscribed
    this.availableSubscriptions = this.AVAILABLE_SUBSCRIPTION_LIST.filter(
      availSub => !subscribedComponents.has(availSub.component)
    );
  }

  private async handleSubscribe(planId: number, companies: number[], personId?: number | null) {
    this.$loadOn();
    SubscriptionService.createPayPalSubscription(planId, companies, personId).then(
        ok => {
          this.$loadOff();
          window.location.href = ok.data;
        },
        err => processError(err, this)
    )
  }

  private isSingleComponent(subscription: MySubscriptionDetailsDTO): boolean {
    return subscription.components.length === 1;
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  private formatPriceCents(priceCents: number): string {
    return (priceCents / 100).toFixed(2);
  }

  private calculateSalesTax(priceCents: number, taxRatePercent: string): string {
    const price = priceCents / 100;
    const taxRate = parseFloat(taxRatePercent) / 100;
    const taxAmount = taxRate * price;
    return taxAmount.toFixed(2);
  }

  private getComponentDescription(commonent: ChargeComponent): string {
    const key = `subscriptions.description.${commonent}`;
    return this.$t(key) as string;
  }

  private getAllCompanies(subscription: MySubscriptionDetailsDTO): string[] {
    // Return the companies array directly from the subscription
    return subscription.companies || [];
  }
  
  private getAllProfiles(subscription: MySubscriptionDetailsDTO): Array<{ name: string; isPerson: boolean }> {
    const profiles: Array<{ name: string; isPerson: boolean }> = [];
    
    // Add person first if present
    if (subscription.personName) {
      profiles.push({ name: subscription.personName, isPerson: true });
    }
    
    // Add all companies
    if (subscription.companies && subscription.companies.length > 0) {
      subscription.companies.forEach(companyName => {
        profiles.push({ name: companyName, isPerson: false });
      });
    }
    
    return profiles;
  }
  
  private handleCallback(action: string, sid: string, component: ChargeComponent) {
    this.isProcessingCallback = true;
    this.callbackAction = action;
    this.callbackComponent = component;
    
    if (action === 'success') {
      // Start polling for permission updates every 30 seconds
      this.startPolling();
    }
    // For cancel, just display the message - no polling needed
  }
  
  private startPolling() {
    // Check immediately
    this.checkAccessUpdate();
    
    // Then check every 30 seconds
    this.pollInterval = window.setInterval(() => {
      this.checkAccessUpdate();
    }, 30000);
  }
  
  private async checkAccessUpdate() {
    try {
      await AuthService.refreshToken();
      
      // Check if access has been granted for the specific component
      const hasAccess = this.callbackComponent && Auth.hasDomainAccess(this.callbackComponent);
      
      if (hasAccess) {
        // Stop polling
        if (this.pollInterval !== null) {
          clearInterval(this.pollInterval);
          this.pollInterval = null;
        }
        
        // Reload subscriptions and update state
        await this.loadSubscriptions();
        this.determineAvailableSubscriptions();
        this.isProcessingCallback = false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Continue polling even if there's an error
    }
  }
  
  private retrySubscription() {
    // Reset callback state and reload the page to show subscription options
    this.isProcessingCallback = false;
    this.callbackAction = null;
    this.$router.push('/my-subscriptions');
  }
  
  private toggleCompanyDropdown(index: number) {
    // Toggle dropdown: if already open, close it; otherwise open it
    if (this.openDropdownIndex === index) {
      this.openDropdownIndex = null;
    } else {
      this.openDropdownIndex = index;
    }
  }
  
  private navigateToPostalRegister(companyName: string) {
    // Find company ID by name in Workspaces
    const workspaces = this.$wss.getAccessibleWorkspaces();
    console.log(companyName);
    console.log('workspaces', workspaces);
    const company = workspaces.find(
      ws => ws.type === WorkspaceType.COMPANY && ws.name === companyName
    );
    
    if (company) {
      // Navigate to postal register with companyId parameter
      this.$router.push({
        name: this.$routeNames.POSTREG_CUSTOMER,
        params: { companyId: company.id.toString() }
      });
    } else {
      console.error('Company not found in workspaces:', companyName);
    }
    
    // Close dropdown after navigation
    this.openDropdownIndex = null;
  }
}
